import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, IndianRupee, HelpingHand, Clock, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDate } from "@/lib/api";

export default function FinanceSupport() {
  const nav = useNavigate();
  const [amount, setAmount] = useState("");
  const [purpose, setPurpose] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/customer/finance-support").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const interest = parseFloat(amount || 0) * 0.12;
  const total = parseFloat(amount || 0) + interest;

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter valid amount"); return; }
    setLoading(true);
    try {
      await api.post("/customer/finance-support", { amount: amt, purpose, interest_percent: 12, repayment_days: 100 });
      toast.success("Request submitted. Awaiting admin review.");
      setAmount(""); setPurpose(""); load();
    } catch (e) { toast.error("Failed"); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="finance-support-page">
      <div className="bg-tenant-gradient text-white px-5 pt-6 pb-6 flex items-center gap-3">
        <button data-testid="header-back" onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white/15 grid place-items-center">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="font-extrabold tracking-tight text-lg">Premium Finance Support</div>
      </div>

      <div className="px-5 pt-6">
        <div className="bg-gradient-to-br from-indigo-500 to-blue-700 text-white rounded-3xl p-5 card-shadow-lg flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-white/20 grid place-items-center"><HelpingHand className="w-7 h-7" /></div>
          <div>
            <div className="font-extrabold tracking-tight">Need help to pay premium?</div>
            <div className="text-[12px] opacity-80">Apply now • 12% interest • 100-day repayment</div>
          </div>
        </div>

        <div className="mt-5">
          <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Amount Required</label>
          <div className="mt-2 flex items-center gap-2 border-2 border-slate-200 rounded-2xl px-4 py-3.5 bg-white">
            <IndianRupee className="w-5 h-5 text-slate-400" />
            <input data-testid="finance-amount" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))} placeholder="0" inputMode="decimal" className="flex-1 outline-none text-xl font-extrabold" />
          </div>
        </div>

        <div className="mt-3">
          <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Purpose (optional)</label>
          <input data-testid="finance-purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="e.g. Annual LIC premium" className="mt-2 w-full border-2 border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-tenant-primary" />
        </div>

        {amount && parseFloat(amount) > 0 && (
          <div className="mt-4 bg-white border border-slate-100 rounded-2xl p-4 card-shadow space-y-1.5 text-sm">
            <Row label="Requested" value={fmtINR(amount)} />
            <Row label="Interest (12%)" value={fmtINR(interest)} />
            <div className="border-t border-slate-100 pt-2 mt-2 flex justify-between"><span className="font-bold">Total Payable</span><span className="font-extrabold text-tenant-primary">{fmtINR(total)}</span></div>
            <Row label="Repayment Period" value="100 Days" />
          </div>
        )}

        <button data-testid="apply-finance-btn" onClick={submit} disabled={loading} className="w-full mt-5 bg-tenant-primary text-white py-3.5 rounded-2xl font-bold tap-scale disabled:opacity-60">
          {loading ? "Submitting..." : "Apply Now"}
        </button>

        <div className="mt-6">
          <h3 className="font-extrabold tracking-tight mb-3">My Requests</h3>
          <div className="space-y-2">
            {items.map((it) => (
              <div key={it.id} className="bg-white border border-slate-100 rounded-2xl p-3 card-shadow flex items-center justify-between">
                <div>
                  <div className="font-bold">{fmtINR(it.amount)}</div>
                  <div className="text-[11px] text-slate-400">{fmtDate(it.created_at)} • {it.purpose || "—"}</div>
                </div>
                <Status status={it.status} />
              </div>
            ))}
            {items.length === 0 && <div className="text-center text-slate-400 text-sm py-6">No requests yet.</div>}
          </div>
        </div>
      </div>
      <div className="h-10" />
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between"><span className="text-slate-500">{label}</span><span className="font-bold">{value}</span></div>
);

const Status = ({ status }) => {
  const map = {
    pending: { c: "bg-amber-50 text-amber-700", I: Clock },
    approved: { c: "bg-emerald-50 text-emerald-700", I: CheckCircle2 },
    rejected: { c: "bg-rose-50 text-rose-700", I: XCircle },
  };
  const { c, I } = map[status] || map.pending;
  return <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${c}`}><I className="w-3 h-3" />{status}</span>;
};
